import axios, { AxiosRequestHeaders } from "axios";
import {
  encryptPayload,
  decryptPayload,
} from "@repo/utilities/utils.crypto.ts";
import { urlUtility } from "@repo/utilities/url.utility.ts";
const apiRequest = axios.create({
  baseURL: import.meta.env.VITE_BASE_API_URL,
  headers: {
    "Content-Type": "application/json",
    timeout: 10000,
  },
  withCredentials: true,
});
function checkEncDec() {
  //If enc-dec disabled through session storage or if the environment is development then no encryption-decryption
  if (import.meta.env.DEV || sessionStorage.getItem("FERC") === "true") {
    return false;
  }
  //If enc-dec is enabled in env
  return import.meta.env.VITE_ENC === "true";
}

function configureHeader(
  headerConfig: AxiosRequestHeaders,
  headersObj: Record<string, string | undefined | null>
) {
  for (const headerName in headersObj) {
    const headerValue = headersObj[headerName];
    if (headerValue !== null && headerValue !== undefined) {
      headerConfig[headerName] = headerValue;
    } else {
      delete headerConfig[headerName];
    }
  }
}

apiRequest.interceptors.request.use((config) => {
  const isEncDecEnabled = checkEncDec();
  if (isEncDecEnabled && config.data) {
    const encryptedData = encryptPayload(config.data);
    config.data = {
      payload: encryptedData,
    };
  }
  if (!isEncDecEnabled) {
    config.headers["X-partner"] = "partner";
  }
  const authToken = localStorage.getItem("T");
  const edmePartnerToken = sessionStorage.getItem("edmePartnerToken");
  const { enq: enquiryId } = urlUtility.getUrlQueries();
  const impersonationToken = localStorage.getItem("impersonationToken");

  configureHeader(config.headers, {
    Authorization: authToken ? `Bearer ${authToken}` : null,
    "Enquiry-Id": enquiryId,
    "X-Partner-Token": edmePartnerToken,
    "X-Impersonation-Token": impersonationToken,
  });

  return config;
});

apiRequest.interceptors.response.use(
  (response) => {
    const isEncDecEnabled = checkEncDec();
    if (isEncDecEnabled && response.data?.response) {
      response.data = decryptPayload(response.data.response);
    }
    return response;
  },
  (error) => {
    const isLoginPage = window.location.pathname.includes("login");
    if (error.status == 401 && !isLoginPage) {
      window.location.href = (import.meta.env.VITE_BASE ?? "/") + "login";
    }
    if (checkEncDec()) {
      const decryptedMessage = (
        decryptPayload(error?.response?.data?.response) as { message?: string }
      )?.message;
      error.response.data.message = decryptedMessage;
    }
    return Promise.reject(error);
  }
);

const apiFormRequest = axios.create({
  baseURL: import.meta.env.VITE_BASE_API_URL,
  headers: {
    "Content-Type": "multipart/form-data",
  },
  withCredentials: true,
});

export function setAuthorizationHeader(token: string) {
  apiRequest.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  apiFormRequest.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  localStorage.setItem("T", token);
}

export function removeHeaderHandler(key: "Enquiry-Id" | "Authorization") {
  delete apiRequest.defaults.headers.common[key];
  delete apiFormRequest.defaults.headers.common[key];
}

export { apiRequest, apiFormRequest };
